'use client';

import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { DropdownArrowIcon } from '@app/app/_icons/DropdownArrowIcon';
import { useClickOutside } from '@app/lib/click-outside/useClickOutside';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { KeyboardEvent, useCallback, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

export function AllInsurancesMenu() {
    const t = useTranslations();
    const parameters = useAppParameters();

    const menuRef = useRef<HTMLButtonElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    const [isOpen, setIsOpen] = useState(false);

    const handleToggle = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            menuRef.current?.focus();
        }
    }, []);

    const handleLinkClick = useCallback(() => {
        handleClose();
    }, [handleClose]);

    useClickOutside(handleClose, [menuRef, popupRef]);

    return (
        <div className="relative">
            <button
                ref={menuRef}
                className={twJoin(
                    'flex cursor-pointer items-center gap-2 text-sm font-medium',
                    isOpen && 'text-primary-500',
                )}
                onClick={handleToggle}
                type="button"
                tabIndex={0}
                aria-label={t('general.allInsurances')}
                aria-haspopup={true}
                aria-controls="all-insurances-menu">
                <Paragraph isBold>{t('general.allInsurances')}</Paragraph>
                <DropdownArrowIcon className={twJoin('size-3.5', isOpen && 'rotate-180')} />
            </button>

            {isOpen && (
                <div
                    ref={popupRef}
                    className={twJoin(
                        'absolute -left-2 top-12 z-20 min-w-48',
                        'rounded-lg bg-white shadow-lg',
                        'animate-in fade-in slide-in-from-top-10',
                    )}
                    tabIndex={-1}
                    onKeyDown={handleKeyDown}
                    role="menu"
                    id="all-insurances-menu">
                    <ul className="flex flex-col p-2">
                        <li>
                            <Link
                                href={parameters.eshopInfoUrls.homeInsurance}
                                target="_blank"
                                onClick={handleLinkClick}
                                className="block rounded px-3 py-2 text-sm hover:bg-neutral-100"
                                role="menuitem">
                                {t('eshop.general.homeInsurance')}
                            </Link>
                        </li>
                        <li>
                            <Link
                                href={parameters.eshopInfoUrls.travelInsurance}
                                target="_blank"
                                onClick={handleLinkClick}
                                className="block rounded px-3 py-2 text-sm hover:bg-neutral-100"
                                role="menuitem">
                                {t('eshop.general.travelInsurance')}
                            </Link>
                        </li>
                        <li>
                            <Link
                                href={parameters.eshopInfoUrls.accidentsInsurance}
                                target="_blank"
                                onClick={handleLinkClick}
                                className="block rounded px-3 py-2 text-sm hover:bg-neutral-100"
                                role="menuitem">
                                {t('eshop.general.accidentInsurance')}
                            </Link>
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
}
